import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Collapse,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import TimerIcon from '@mui/icons-material/Timer';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import Infographic from './Infographic';

function CourseModule({ 
  module, 
  expanded, 
  onChange, 
  completedLessons = [], 
  currentLesson,
  onStartLesson,
  courseName // Add courseName prop to identify which course is using this component
}) {
  const navigate = useNavigate();
  const [showCompletionDialog, setShowCompletionDialog] = useState(false);
  const lessons = module?.lessons || [];  // Add default value
  
  // Count only non-header lessons for progress calculation
  const nonHeaderLessons = lessons.filter(lesson => !lesson.isHeader);
  const totalLessons = nonHeaderLessons.length;
  const completedCount = nonHeaderLessons.filter(lesson => 
    completedLessons.includes(lesson.id)
  ).length;
  const progress = totalLessons > 0 ? (completedCount / totalLessons) * 100 : 0;

  useEffect(() => {
    // Check if all non-header lessons are completed and show dialog
    if (completedCount === totalLessons && totalLessons > 0) {
      setShowCompletionDialog(true);
    }
  }, [completedCount, totalLessons]);

  const handleCloseDialog = () => {
    setShowCompletionDialog(false);
  };

  return (
    <Paper 
      elevation={2} 
      sx={{ 
        borderRadius: 2,
        overflow: 'hidden'
      }}
    >
      <Box
        onClick={onChange}
        sx={{
          p: 3,
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          backgroundColor: expanded ? 'primary.light' : 'background.paper',
          transition: 'background-color 0.3s ease',
          '&:hover': {
            backgroundColor: expanded ? 'primary.light' : 'action.hover'
          }
        }}
      >
        <Box sx={{ mr: 2 }}>
          {module.icon}
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: expanded ? 'primary.main' : 'text.primary' }}>
            {module.title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {module.description}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
            <AccessTimeIcon sx={{ fontSize: 18, mr: 0.5, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              {module.duration} minutes
            </Typography>
          </Box>
        </Box>
        <IconButton
          sx={{
            transform: expanded ? 'rotate(180deg)' : 'rotate(0)',
            transition: 'transform 0.3s ease',
          }}
        >
          <ExpandMoreIcon />
        </IconButton>
      </Box>
      <Collapse in={expanded}>
        <Box sx={{ p: 3, backgroundColor: 'background.paper' }}>
          <List sx={{ width: '100%' }}>
            {/* First render the header */}
            {lessons.filter(lesson => lesson.isHeader).map((header, index) => (
              <Typography 
                key={`header-${index}`}
                variant="h6"
                sx={{ 
                  mt: index === 0 ? 0 : 4,
                  mb: 2, 
                  fontWeight: 600, 
                  color: 'text.primary' 
                }}
              >
                {header.title}
              </Typography>
            ))}
            
            {/* Then render all non-header lessons */}
            {lessons.filter(lesson => !lesson.isHeader).map((lesson, index) => {
              const isCompleted = completedLessons.includes(lesson.id);
              const isCurrent = currentLesson === lesson.id;
              
              return (
                <ListItem
                  key={lesson.id}
                  sx={{
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    mb: 2,
                    borderRadius: 2,
                    transition: 'all 0.3s ease',
                  }}
                >
                  <Box sx={{ display: 'flex', width: '100%', alignItems: 'center' }}>
                    <ListItemIcon>
                      {isCompleted ? (
                        <CheckCircleIcon color="success" />
                      ) : (
                        <RadioButtonUncheckedIcon color="disabled" />
                      )}
                    </ListItemIcon>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1">
                        {`${index + 1}. ${lesson.title}`}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {lesson.description}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Chip 
                          size="small"
                          icon={<TimerIcon />}
                          label={`${lesson.duration} min`}
                        />
                        {lesson.type && (
                          <Chip 
                            size="small"
                            label={lesson.type}
                          />
                        )}
                      </Box>
                    </Box>
                    <Button
                      variant={isCurrent ? "contained" : "outlined"}
                      color={isCompleted ? "success" : "primary"}
                      onClick={(e) => {
                        // Check if this is the BLSCPR course
                        if (courseName === 'blscpr') {
                          // For BLSCPR course, pass the lesson object
                          onStartLesson(lesson);
                        } else {
                          // For Fall Prevention Protocol and other courses, pass moduleId and lessonId separately
                          onStartLesson(module.id, lesson.id);
                        }
                      }}
                      sx={{ ml: 2 }}
                    >
                      {isCompleted ? "Review" : "Start"}
                    </Button>
                  </Box>
                </ListItem>
              );
            })}
          </List>
        </Box>
      </Collapse>
    </Paper>
  );
}

export default CourseModule;
